/**
 * Maps a Prismic `seo_content_component` payload into the shape consumed by
 * the PLP SEO accordion (`_components/PLP/SeoContentAccordion`).
 *
 * Two input shapes are supported so both render sites can share this util:
 *  - the pre-built JSON dictionary entry (`data`) read by getSeoComponentByPath
 *  - a branded-store slice `primary` (which only carries the legacy fields)
 *
 * New content authored in Prismic uses the repeatable `accordion_items` group.
 * Legacy documents (the ~1000 existing PLP SEO docs) have no group — their
 * single markdown `body` typically begins with a `## heading`, so we split that
 * leading heading off as the accordion header and keep the rest as the body,
 * yielding one item that is open by default. This is the render-time migration:
 * existing content adopts the new styling with no manual re-configuration.
 */

interface PrismicTextBlock {
  type?: string;
  text?: string;
}

interface PrismicAccordionItem {
  heading?: string | null;
  body?: PrismicTextBlock[] | null;
  open_by_default?: boolean | null;
}

export interface RawSeoComponent {
  title?: string | null;
  // The pre-built JSON dictionary carries StructuredText blocks, while a
  // branded-store slice `primary` carries an already-flattened markdown string.
  body?: string | PrismicTextBlock[] | null;
  body1?: { text?: string | null }[] | null;
  accordion_items?: PrismicAccordionItem[] | null;
}

export interface SeoAccordionItem {
  heading: string;
  body: string;
  openByDefault: boolean;
}

export interface SeoAccordionData {
  items: SeoAccordionItem[];
}

interface MapOptions {
  /** Heading used when a legacy body has no leading markdown heading or title. */
  fallbackHeading?: string;
}

const getStructuredText = (blocks?: PrismicTextBlock[] | null): string =>
  (blocks ?? [])
    .map((block) => block?.text ?? '')
    .join('\n\n')
    .trim();

/**
 * Inserts the CommonMark-required space after a leading ATX marker so
 * mis-authored legacy copy like `###Heading` (no space) still renders as a
 * heading instead of literal text. Well-formed `### Heading` and non-heading
 * lines are left untouched.
 */
const normalizeHeadings = (markdown: string): string =>
  markdown.replace(/^(\s*#{1,6})(?=[^\s#])/gm, '$1 ');

/** Resolves the legacy body, accepting a string, StructuredText blocks or body1. */
const getLegacyBody = (raw: RawSeoComponent): string => {
  const fromBody =
    typeof raw.body === 'string'
      ? raw.body.trim()
      : getStructuredText(raw.body);
  if (fromBody) {
    return fromBody;
  }
  return (raw.body1?.[0]?.text ?? '').trim();
};

/**
 * Splits a leading markdown heading (`#`..`######`) off the top of a body.
 * Returns an empty heading when the body does not start with one.
 */
const splitLeadingHeading = (
  markdown: string
): { heading: string; body: string } => {
  const lines = markdown.split('\n');
  let index = 0;
  while (index < lines.length && lines[index].trim() === '') {
    index += 1;
  }

  const match = lines[index]?.match(/^#{1,6}\s+(.*\S)\s*$/);
  if (match) {
    return {
      heading: match[1].trim(),
      body: lines
        .slice(index + 1)
        .join('\n')
        .trim(),
    };
  }

  return { heading: '', body: markdown.trim() };
};

const mapSeoAccordionData = (
  raw?: RawSeoComponent | null,
  options: MapOptions = {}
): SeoAccordionData | null => {
  if (!raw) {
    return null;
  }

  const groupItems = raw.accordion_items ?? [];

  if (groupItems.length > 0) {
    const parsed = groupItems
      .map((item) => ({
        heading: (item.heading ?? '').trim(),
        body: normalizeHeadings(getStructuredText(item.body)),
        rawOpen: item.open_by_default === true,
      }))
      .filter((item) => item.heading || item.body);

    if (parsed.length > 0) {
      // Requirement: first item open by default; Prismic can override per item.
      // When no item is explicitly opened, fall back to opening the first.
      const anyExplicitlyOpen = parsed.some((item) => item.rawOpen);
      return {
        items: parsed.map((item, index) => ({
          heading: item.heading,
          body: item.body,
          openByDefault: anyExplicitlyOpen ? item.rawOpen : index === 0,
        })),
      };
    }
  }

  // Legacy fallback — synthesize a single open item from the existing body.
  // Normalize before the leading-heading split so a mis-authored `###Heading`
  // is promoted to the accordion header rather than left inside the body.
  const legacyBody = normalizeHeadings(getLegacyBody(raw));
  const title = (raw.title ?? '').trim();

  if (!legacyBody && !title) {
    return null;
  }

  const { heading: extractedHeading, body: remainder } =
    splitLeadingHeading(legacyBody);

  const heading = extractedHeading || title || options.fallbackHeading || '';
  const body = extractedHeading ? remainder : legacyBody;

  if (!heading && !body) {
    return null;
  }

  return {
    items: [
      {
        heading,
        body,
        openByDefault: true,
      },
    ],
  };
};

export default mapSeoAccordionData;
